import React, { useEffect, useRef, useState } from "react";
import { HiddenVisually, Image, Stack, Text, useTheme } from "@bookingcom/bui-react";
import styles from "./GeniusAirlinesBanner.module.css";
import { GeniusAirline, UIGeniusCampaignBanner } from "@flights/types";
import { Trans } from "@bookingcom/lingojs-react";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useGeniusAirlinesBannerEvent from "../../../hooks/c360/useGeniusAirlinesBannerEvent";
import useIsInViewport from "../../../hooks/useIsInViewport";
import useGeniusAirlinesBanner from "../../../utils/experiments/genius/web_flights_lve_index_genius_airlines_banner";

interface Props {
  geniusAirlineBannerData: UIGeniusCampaignBanner;
}

const GeniusAirlinesBannerMobile: React.FC<Props> = (props) => {
  const { geniusAirlineBannerData } = props;
  const airlines = geniusAirlineBannerData.airlines;
  const theme = useTheme();

  const trackGeniusAirlinesBannerEvent = useGeniusAirlinesBannerEvent();
  useEffect(() => {
    if (isFeatureRunningClientSide("FLIGHTS_LVE_ENABLE_SUBMIT_C360_EVENTS_FOR_GENIUS_AIRLINES_BANNER")) {
      trackGeniusAirlinesBannerEvent(airlines.map((airline) => airline.iataCode));
    }
  }, [trackGeniusAirlinesBannerEvent, airlines]);

  // Start - web_flights_lve_index_genius_airlines_banner
  const geniusAirlinesBanner = useGeniusAirlinesBanner();
  const carriersRef = useRef<HTMLElement>(null);
  const isInViewport = useIsInViewport(carriersRef.current);

  useEffect(() => {
    const trackExperimentRender = () => {
      geniusAirlinesBanner.trackCustomGoalWithValueBannerRendered();
    };
    window.addEventListener("load", trackExperimentRender);
    return () => window.removeEventListener("load", trackExperimentRender);
  }, [geniusAirlinesBanner]);

  const [alreadyTrackedViewed, setAlreadyTrackedViewed] = useState(false);
  useEffect(() => {
    // Only tracked view once per render. Other wise scroll up/down on the page would trigger the view multiple times
    if (isInViewport && !alreadyTrackedViewed) {
      geniusAirlinesBanner.trackCustomGoalWithValueBannerViewed();
      setAlreadyTrackedViewed(true);
    }
  }, [isInViewport, geniusAirlinesBanner, alreadyTrackedViewed]);
  // End - web_flights_lve_index_genius_airlines_banner

  const renderCarrierList = () => {
    const firstThreeAirlines = airlines.slice(0, 3);
    const secondThreeAirlines = airlines.slice(3, 6);

    const renderCarrierItem = ({ name, imageUrl }: GeniusAirline) =>
      !!name &&
      !!imageUrl && (
        <Stack.Item key={name} className={styles.airlinesLogoWrapperAsPrimaryOffer} attributes={{ role: "listitem" }}>
          <Stack direction="column" justifyContent="space-between" alignItems="center" gap={2}>
            <Image width="32px" src={imageUrl} aria-hidden="true" alt="" />
            <Text variant="small_2" wrap="balance" maxLines={1} bidirectional>
              {name}
            </Text>
          </Stack>
        </Stack.Item>
      );

    return (
      <Stack direction="row" justifyContent="space-between" attributes={{ role: "list", ref: carriersRef }}>
        {firstThreeAirlines.map(renderCarrierItem)}
        {secondThreeAirlines.map(renderCarrierItem)}
      </Stack>
    );
  };

  // Handle banner click via attributes
  const handleBannerClick = () => {
    geniusAirlinesBanner.trackCustomGoalBannerClicked();
  };

  return (
    <>
      <Stack
        direction={"column"}
        gap={3}
        className={styles.container}
        alignItems={"start"}
        justifyContent="space-between"
        attributes={{ onClick: handleBannerClick }}
      >
        <Stack.Item className={styles.bannerText}>
          <Text variant="strong_1">
            <HiddenVisually tagName={"h3"}>
              <Trans
                aria-hidden
                tag="flights_index_genius_airline_banner_title"
                variables={{
                  start_styling: "",
                  end_styling: ""
                }}
              />
            </HiddenVisually>
            <div aria-hidden>
              <Trans
                tag="flights_index_genius_airline_banner_title"
                variables={{
                  start_styling: "<0>",
                  end_styling: "</0>"
                }}
                components={[<span key={0} style={{ color: theme.colors.color_brand_primary_background }} />]}
              />
            </div>
          </Text>
          <Text variant="body_2" className={styles.subtitle}>
            <Trans tag="flights_index_genius_airline_banner_body" />
          </Text>
        </Stack.Item>
        <Stack.Item mixin={{ width: "100%" }}>{renderCarrierList()}</Stack.Item>
      </Stack>
      <Text variant="small_2" align="start" className={styles.textBelowBanner} color="neutral_alt">
        <Trans tag="flights_lve_airline_promotions_genius_tooltip" />
      </Text>
    </>
  );
};

export default GeniusAirlinesBannerMobile;
